import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

export type CodeEntryMode = 'numeric' | 'alphanumeric';

/**
 * CodeEntryComponent — one input cell per character, with the interaction
 * model operators expect from a one-time-code field: typing advances, Backspace
 * on an empty cell hops back and clears, arrows move, and a paste of the full
 * code into ANY cell fills every cell (a shorter paste fills from the targeted
 * cell). Story 47.1 extracted this from emjo-login's DeviceComponent (where it
 * was inline and hard-wired to RFC 8628's 8-char XXXX-XXXX user_code) so the
 * admin console's MFA challenge and enrollment screens (6 numeric digits) share
 * ONE implementation instead of a second copy of the paste-handling logic.
 *
 * Shared across both Angular projects via projects/shared (relative import,
 * like shared/i18n). Deliberately framework-light: no Transloco dependency —
 * labels come in as inputs so each app localizes its own way.
 *
 *   - `length`      number of cells (device: 8, MFA: 6)
 *   - `mode`        'numeric' (0-9) or 'alphanumeric' (A-Z0-9, forced uppercase)
 *   - `groupSize`   > 0 renders a hyphen before each group boundary (device: 4)
 *   - `initialValue` pre-fills (dash/separator-tolerant, truncated to length)
 *   - `completed`   emits the assembled value the moment every cell is filled
 */
@Component({
  selector: 'app-code-entry',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="code-inputs" role="group" [attr.aria-label]="ariaLabel">
      @for (ctrl of controls; track $index) {
        @if (showHyphenBefore($index)) {
          <span class="code-hyphen" aria-hidden="true">-</span>
        }
        <input
          #cell
          class="code-cell"
          type="text"
          [attr.inputmode]="mode === 'numeric' ? 'numeric' : 'text'"
          [attr.autocapitalize]="mode === 'alphanumeric' ? 'characters' : 'off'"
          autocomplete="one-time-code"
          maxlength="1"
          [formControl]="ctrl"
          [attr.aria-label]="labelFor($index)"
          (input)="onCellInput($index, $event)"
          (keydown)="onCellKeydown($index, $event)"
          (paste)="onCellPaste($index, $event)"
        />
      }
    </div>
  `,
  styles: [`
    .code-inputs {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 8px 0 24px;
    }
    .code-cell {
      width: 40px;
      height: 52px;
      padding: 0;
      text-align: center;
      font-size: 1.5rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--mat-sys-on-surface, #1a1a1a);
      background: var(--mat-sys-surface, #fff);
      border: 1px solid var(--mat-sys-outline, #999);
      border-radius: 8px;
      caret-color: var(--mat-sys-primary, #1976d2);
    }
    .code-cell:focus {
      outline: none;
      border-color: var(--mat-sys-primary, #1976d2);
      box-shadow: 0 0 0 2px var(--mat-sys-primary, #1976d2);
    }
    .code-cell:disabled {
      opacity: 0.6;
    }
    .code-hyphen {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--mat-sys-on-surface-variant, #777);
      padding: 0 2px;
    }
    @media (max-width: 420px) {
      .code-inputs { gap: 5px; }
      .code-cell { width: 32px; height: 44px; font-size: 1.25rem; }
    }
  `],
})
export class CodeEntryComponent implements AfterViewInit {
  private _length = 6;
  private pendingInitial = '';

  /** Number of cells. Rebuilds the controls when it changes. */
  @Input()
  set length(n: number) {
    const next = Math.max(1, Math.floor(Number(n) || 0));
    if (next === this._length && this.controls.length === next) {
      return;
    }
    this._length = next;
    this.controls = Array.from({length: next}, () => new FormControl('', {nonNullable: true}));
    if (this.pendingInitial) {
      this.fill(this.pendingInitial);
    }
  }
  get length(): number {
    return this._length;
  }

  @Input() mode: CodeEntryMode = 'numeric';
  @Input() groupSize = 0;
  @Input() ariaLabel = 'Verification code';
  /** `{n}` and `{total}` are substituted per cell. */
  @Input() cellLabel = 'Character {n} of {total}';
  @Input() autofocus = false;

  @Input()
  set initialValue(v: string | null | undefined) {
    this.pendingInitial = v ?? '';
    if (this.pendingInitial) {
      this.fill(this.pendingInitial);
    }
  }

  @Input()
  set disabled(v: boolean) {
    for (const c of this.controls) {
      if (v) {
        c.disable({emitEvent: false});
      } else {
        c.enable({emitEvent: false});
      }
    }
  }

  /** Emits the current assembled value on every change. */
  @Output() readonly valueChange = new EventEmitter<string>();
  /** Emits the assembled value once every cell holds a character. */
  @Output() readonly completed = new EventEmitter<string>();

  controls: FormControl<string>[] = Array.from({length: this._length}, () => new FormControl('', {nonNullable: true}));

  @ViewChildren('cell') private cells!: QueryList<ElementRef<HTMLInputElement>>;

  ngAfterViewInit(): void {
    if (this.autofocus) {
      // Defer past the current change-detection pass.
      setTimeout(() => this.focusCell(0));
    }
  }

  // --- public API ----------------------------------------------------------

  /** The cells joined, no separators. */
  value(): string {
    return this.controls.map(c => c.value).join('');
  }

  /** True once every cell holds a character. */
  complete(): boolean {
    return this.controls.every(c => c.value.length === 1);
  }

  /** Fill from a string (separators stripped, normalized, truncated to length). */
  fill(code: string): void {
    const chars = this.normalize(code).slice(0, this._length).split('');
    this.controls.forEach((c, i) => c.setValue(chars[i] ?? ''));
    this.emitChange();
  }

  reset(): void {
    this.controls.forEach(c => c.setValue(''));
    this.emitChange();
    this.focusCell(0);
  }


  showHyphenBefore(index: number): boolean {
    return this.groupSize > 0 && index > 0 && index % this.groupSize === 0;
  }

  labelFor(index: number): string {
    return this.cellLabel.replace('{n}', String(index + 1)).replace('{total}', String(this._length));
  }

  // --- cell interaction ----------------------------------------------------

  onCellInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const text = this.normalize(input.value);
    // A MULTI-character value in a single input event is not a typo — it is
    // Android/Chrome one-time-code autofill, an IME composition commit, speech
    // input or a drag-and-drop, none of which fire a `paste` event. We advertise
    // autocomplete="one-time-code" on every cell, so autofill delivering the
    // whole code here is an expected path, not an exotic one. Keeping only the
    // last character (the previous behaviour) silently discarded digits 1..n-1
    // and left the operator staring at one wrong digit. Spread it instead,
    // exactly as onCellPaste does.
    if (text.length > 1) {
      this.fillFrom(text.length >= this._length ? 0 : index, text);
      input.value = this.controls[index].value ?? '';
      this.emitChange();
      return;
    }
    const char = text.slice(-1);
    this.controls[index].setValue(char);
    input.value = char; // reflect the normalized value immediately
    if (char && index < this._length - 1) {
      this.focusCell(index + 1);
    }
    this.emitChange();
  }

  /** Writes `text` across the cells starting at `start`, then parks the focus. */
  private fillFrom(start: number, text: string): void {
    let cursor = start;
    for (const ch of text) {
      if (cursor >= this._length) {
        break;
      }
      this.controls[cursor].setValue(ch);
      cursor++;
    }
    this.focusCell(Math.min(cursor, this._length - 1));
  }

  onCellKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.controls[index].value && index > 0) {
      // Empty cell → hop back and clear the previous one.
      event.preventDefault();
      this.controls[index - 1].setValue('');
      this.focusCell(index - 1);
      this.emitChange();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusCell(index - 1);
    } else if (event.key === 'ArrowRight' && index < this._length - 1) {
      event.preventDefault();
      this.focusCell(index + 1);
    }
  }

  onCellPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const text = this.normalize(event.clipboardData?.getData('text') ?? '');
    if (!text) {
      return;
    }
    // A full-length paste fills the whole code from the first cell no matter
    // which cell was targeted; a shorter paste fills from the current cell.
    this.fillFrom(text.length >= this._length ? 0 : index, text);
    this.emitChange();
  }

  // --- internals -----------------------------------------------------------

  private normalize(raw: string): string {
    return this.mode === 'numeric'
      ? raw.replace(/[^0-9]/g, '')
      : raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  private emitChange(): void {
    const v = this.value();
    this.valueChange.emit(v);
    if (this.complete()) {
      this.completed.emit(v);
    }
  }

  private focusCell(index: number): void {
    const el = this.cells?.get(index)?.nativeElement;
    if (el) {
      el.focus();
      el.select();
    }
  }
}
